import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Pin,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import {
  useAnnouncementsAdmin,
  useDeleteAnnouncement,
  type Announcement,
} from "../../../services/announcementService";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { Badge } from "../../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";

const Announcements: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] =
    useState<Announcement | null>(null);

  const queryParams = {
    per_page: 12,
    page: currentPage,
    locale: currentLanguage,
    ...(searchTerm && { search: searchTerm }),
  };

  const {
    data: announcementsResponse,
    isLoading,
    error,
    refetch,
  } = useAnnouncementsAdmin(queryParams);

  const deleteAnnouncementMutation = useDeleteAnnouncement();

  const announcements = announcementsResponse?.announcements || [];
  const pagination = announcementsResponse?.pagination;

  const handleDeleteAnnouncement = (announcement: Announcement) => {
    setAnnouncementToDelete(announcement);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteAnnouncement = async () => {
    if (!announcementToDelete) return;

    try {
      await deleteAnnouncementMutation.mutateAsync(
        parseInt(announcementToDelete.id)
      );
      setIsDeleteDialogOpen(false);
      setAnnouncementToDelete(null);
    } catch (error) {
      console.error("Failed to delete announcement:", error);
    }
  };

  const cancelDeleteAnnouncement = () => {
    setIsDeleteDialogOpen(false);
    setAnnouncementToDelete(null);
  };

  const handleEditAnnouncement = (announcement: Announcement) => {
    navigate(`/${currentLanguage}/admin/announcements/${announcement.id}/edit`);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString(currentLanguage, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {t("sidebar.announcements", "Announcements")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t(
                "admin.announcements.subtitle",
                "Manage and monitor all announcements"
              )}
            </p>
          </div>
          <Link
            to={`/${currentLanguage}/admin/announcements/create`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            {t("admin.announcements.createNew", "Create New Announcement")}
          </Link>
        </div>

        {/* Filters and Search */}
        <Card className="bg-white dark:bg-gray-800 border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder={t(
                      "admin.announcements.searchPlaceholder",
                      "Search announcements..."
                    )}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10 py-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card className="bg-white dark:bg-gray-800 border-red-200 dark:border-red-900">
            <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
              <AlertCircle className="w-12 h-12 text-red-500" />
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t("common.error_loading_data", "Error Loading Data")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md">
                  {t(
                    "admin.announcements.errorLoading",
                    "Error loading announcements. Please try again later."
                  )}
                </p>
              </div>
              <Button
                onClick={() => refetch()}
                className="flex items-center gap-2"
                variant="outline"
              >
                <RefreshCw className="w-4 h-4" />
                {t("common.retry", "Retry")}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Announcements Grid */}
        {!isLoading && !error && (
          <>
            {announcements.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {announcements.map((announcement) => (
                  <Card
                    key={announcement.id}
                    className="bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
                  >
                    {announcement.is_pinned && (
                      <div className="h-1 bg-gradient-to-r from-red-500 to-pink-500" />
                    )}
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2 flex-1">
                          {announcement.title}
                        </CardTitle>
                        {announcement.is_pinned && (
                          <Badge
                            variant="secondary"
                            className="flex items-center gap-1 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                          >
                            <Pin className="w-3 h-3" />
                            {t("admin.announcements.pinned", "Pinned")}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                        {announcement.body}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <span>
                          {announcement.created_at
                            ? formatDate(announcement.created_at)
                            : ""}
                        </span>
                        {announcement.created_by && (
                          <span>{announcement.created_by.name}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditAnnouncement(announcement)}
                          className="flex-1"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {t("admin.announcements.edit", "Edit")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteAnnouncement(announcement)}
                          disabled={deleteAnnouncementMutation.isPending}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-white dark:bg-gray-800">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {t(
                      "admin.announcements.noAnnouncements",
                      "No announcements found. Create your first announcement to get started."
                    )}
                  </p>
                  <Link
                    to={`/${currentLanguage}/admin/announcements/create`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <Plus className="w-5 h-5" />
                    {t(
                      "admin.announcements.createNew",
                      "Create New Announcement"
                    )}
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <Button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                >
                  {t("common.previous", "Previous")}
                </Button>
                <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
                  {t("common.page", "Page")} {currentPage}{" "}
                  {t("common.of", "of")} {pagination.last_page}
                </span>
                <Button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(pagination.last_page, p + 1))
                  }
                  disabled={currentPage === pagination.last_page}
                  variant="outline"
                >
                  {t("common.next", "Next")}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("admin.announcements.confirmDelete", "Delete Announcement")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "admin.announcements.confirmDeleteDescription",
                  "Are you sure you want to delete this announcement? This action cannot be undone."
                )}
              </DialogDescription>
            </DialogHeader>
            {announcementToDelete && (
              <div className="py-4">
                <p className="font-semibold text-gray-900 dark:text-white">
                  {announcementToDelete.title}
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={cancelDeleteAnnouncement}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteAnnouncement}
                disabled={deleteAnnouncementMutation.isPending}
              >
                {deleteAnnouncementMutation.isPending
                  ? t("common.deleting", "Deleting...")
                  : t("admin.announcements.delete", "Delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Announcements;
